import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, CircularProgress, Collapse, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import type { AVProgressItem } from './ChatWindowTypes';

// Hook for animated dots
const useDotCount = (enabled: boolean) => {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      setDotCount((prevCount) => (prevCount + 1) % 4);
    }, 500);
    return () => clearInterval(interval);
  }, [enabled]);

  return { dotCount: enabled ? dotCount : 0 };
};

const getActionLabel = (item: AVProgressItem): string => {
  const name = item.name;
  switch (item.action) {
    case 'add':
      if (item.status === 'searching') {
        return `Searching ${name}`;
      }
      return `Add ${name}`;
    case 'modify':
      return `Update ${name}`;
    case 'remove':
      return `Remove ${name}`;
    case 'study_period':
      return 'Update study period';
    default:
      return name;
  }
};

const CHECK_SIZE = 14;
const SPINNER_SIZE = 14;

/** Stepper indicator — spinner (active), CheckCircleIcon (done), grey hollow (pending), CancelIcon (failed) */
const StepperIndicator = ({ item, isLast }: { item: AVProgressItem; isLast: boolean }) => {
  const theme = useTheme();
  const isActive =
    item.status === 'in_progress' || item.status === 'searching' || item.status === 'retrying';
  const isDone = item.status === 'completed';

  const connectingLine = !isLast && (
    <Box
      sx={{
        width: 2,
        flexGrow: 1,
        minHeight: 4,
        backgroundColor: theme.palette.divider,
        mt: '2px',
      }}
    />
  );

  // Determine the indicator icon
  let indicator;
  if (isDone) {
    indicator = (
      <CheckCircleIcon
        sx={{ fontSize: CHECK_SIZE, color: theme.palette.success.main, flexShrink: 0 }}
      />
    );
  } else if (isActive) {
    indicator = <CircularProgress size={SPINNER_SIZE} sx={{ flexShrink: 0 }} />;
  } else if (item.status === 'failed') {
    indicator = (
      <CancelIcon sx={{ fontSize: SPINNER_SIZE, color: theme.palette.error.main, flexShrink: 0 }} />
    );
  } else {
    // Pending — hollow dot
    indicator = (
      <Box
        sx={{
          width: SPINNER_SIZE,
          height: SPINNER_SIZE,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          border: `2px solid ${theme.palette.action.disabled}`,
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 20,
        flexShrink: 0,
        mt: '4px',
      }}
    >
      {indicator}
      {connectingLine}
    </Box>
  );
};

interface ChatWindowProgressProps {
  message: string;
  avProgress?: AVProgressItem[] | null;
  /** When true, progress is complete — no spinner/dots, static display */
  isComplete?: boolean;
}

export const ChatWindowProgress = ({
  message,
  avProgress,
  isComplete = false,
}: ChatWindowProgressProps) => {
  const theme = useTheme();
  const showDots = !isComplete;
  const { dotCount } = useDotCount(showDots);
  const [isExpanded, setIsExpanded] = useState(false);
  const prevMessageRef = useRef(message);

  const hasAVProgress = avProgress && avProgress.length > 0;
  const prevHadProgress = useRef(false);

  // Auto-expand when AV progress items first appear — skip if already complete (e.g. on reload)
  useEffect(() => {
    if (hasAVProgress && !prevHadProgress.current && !isComplete) {
      setIsExpanded(true);
    }
    prevHadProgress.current = !!hasAVProgress;
  }, [hasAVProgress, isComplete]);

  // Auto-collapse when the top-level step changes (e.g., "Defining" → "Assembling")
  useEffect(() => {
    if (prevMessageRef.current !== message) {
      prevMessageRef.current = message;
      setIsExpanded(false);
      prevHadProgress.current = false;
    }
  }, [message]);

  // Completed state: show "Made N updates" as the final message
  const completedCount = hasAVProgress ? avProgress.length : 0;
  const displayMessage = isComplete
    ? `Made ${completedCount} update${completedCount !== 1 ? 's' : ''}`
    : message;

  // Only show the top-level spinner when the checklist is NOT expanded (or no checklist)
  const showTopSpinner = !isComplete && (!hasAVProgress || !isExpanded);

  // Consistent text style for all progress text
  const progressTextSx = {
    color: theme.palette.text.secondary,
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: '100%',
        padding: `${theme.spacing(1)} 0`,
      }}
    >
      {/* Top-level progress */}
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={1.5}
        sx={{
          cursor: hasAVProgress ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={hasAVProgress ? () => setIsExpanded(!isExpanded) : undefined}
      >
        {showTopSpinner && <CircularProgress size={16} />}
        <Typography variant="body2" sx={{ flex: 1, ...progressTextSx }}>
          {displayMessage}
          {showDots &&
            Array(dotCount)
              .fill('.')
              .map((dot, index) => <span key={index}>{dot}</span>)}
        </Typography>
        {hasAVProgress &&
          (isExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ))}
      </Stack>

      {/* Expanded: vertical stepper */}
      {hasAVProgress && (
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 1, ml: 0.5 }}>
            {[...avProgress]
              .sort((a, b) => a.avIndex - b.avIndex)
              .map((item, idx) => (
                <Stack key={item.avIndex} direction="row" alignItems="flex-start" spacing={1}>
                  <StepperIndicator item={item} isLast={idx === avProgress.length - 1} />
                  <Box
                    sx={{
                      pb: idx === avProgress.length - 1 ? 0 : 0.75,
                    }}
                  >
                    <Typography variant="body2" sx={progressTextSx}>
                      {getActionLabel(item)}
                      {item.status === 'retrying' && item.retryMessage
                        ? ` — ${item.retryMessage}`
                        : ''}
                    </Typography>
                  </Box>
                </Stack>
              ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
